import React from 'react';
import { Paper, Avatar, Box, Typography, Link } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import { useTranslation } from 'react-i18next';

const HEADING_FONT = "'Manrope', sans-serif";
const BODY_FONT = "'Inter', sans-serif";

const linkSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  p: 2,
  borderRadius: 2,
  bgcolor: '#f3f4f5',
  color: 'text.primary',
  textDecoration: 'none',
  fontFamily: BODY_FONT,
  fontSize: '0.875rem',
  fontWeight: 500,
  transition: 'all 0.2s',
  '&:hover': {
    bgcolor: '#c6e7ff',
    '& .link-icon': {
      transform: 'scale(1.1)',
    },
  },
};

export default function SalesContactCard({ salesContact, variant = 'full', sx }) {
  const { t } = useTranslation();

  if (!salesContact) return null;

  const { name, email, phone, photo_url } = salesContact;
  const initials = name ? name.charAt(0).toUpperCase() : '?';

  // Build tel: href with extension support (e.g. tel:+380487993997,2)
  const buildTelHref = (phoneStr) => {
    if (!phoneStr) return '';
    // Parse "phone ext. 123" or "phone,123" formats
    const extMatch = phoneStr.match(/^(.+?)\s*(?:ext\.?\s*|,)(\d+)$/);
    if (extMatch) {
      return `tel:${extMatch[1].trim()},${extMatch[2]}`;
    }
    return `tel:${phoneStr}`;
  };
  const telHref = buildTelHref(phone);

  if (variant === 'compact') {
    return (
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 2,
          borderRadius: 2,
          boxShadow: '0 12px 40px rgba(25,28,29,0.06)',
          border: '1px solid rgba(187,203,184,0.12)',
          ...sx,
        }}
      >
        <Avatar
          src={photo_url || undefined}
          sx={{ width: 56, height: 56, flexShrink: 0, bgcolor: '#00C853' }}
        >
          {!photo_url && initials}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontFamily: HEADING_FONT, fontWeight: 700 }} noWrap>
            {name}
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: BODY_FONT, color: '#00658d', fontWeight: 500 }} noWrap>
            {t('offer.salesRole', 'Ringostat Expert')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
          {phone && (
            <Link
              href={telHref}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: '#f3f4f5',
                color: '#00658d',
                transition: 'all 0.2s',
                '&:hover': { bgcolor: '#c6e7ff' },
              }}
            >
              <PhoneIcon sx={{ fontSize: 18 }} />
            </Link>
          )}
          <Link
            href={`mailto:${email}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: '#f3f4f5',
              color: '#00658d',
              transition: 'all 0.2s',
              '&:hover': { bgcolor: '#c6e7ff' },
            }}
          >
            <EmailIcon sx={{ fontSize: 18 }} />
          </Link>
        </Box>
      </Paper>
    );
  }

  // Full variant (desktop)
  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
        bgcolor: '#fff',
        border: '1px solid rgba(187,203,184,0.12)',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
        },
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 4 }}>
        <Avatar
          src={photo_url || undefined}
          sx={{
            width: 96,
            height: 96,
            bgcolor: '#00C853',
            fontSize: '2.25rem',
            fontFamily: HEADING_FONT,
            fontWeight: 700,
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          }}
        >
          {!photo_url && initials}
        </Avatar>
        <Box>
          <Typography
            sx={{
              fontFamily: HEADING_FONT,
              fontWeight: 700,
              fontSize: '1.25rem',
            }}
          >
            {name}
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontFamily: BODY_FONT, color: '#00658d', fontWeight: 500, fontSize: '0.875rem' }}
          >
            {t('offer.salesRole', 'Ringostat Expert')}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {phone && (
          <Link href={telHref} sx={linkSx}>
            <PhoneIcon className="link-icon" sx={{ fontSize: 20, color: '#00658d', transition: 'transform 0.2s' }} />
            {phone}
          </Link>
        )}
        <Link href={`mailto:${email}`} sx={linkSx}>
          <EmailIcon className="link-icon" sx={{ fontSize: 20, color: '#00658d', transition: 'transform 0.2s' }} />
          {email}
        </Link>
      </Box>
    </Paper>
  );
}
